import React, { useState } from 'react';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://avocado-license-api.personal-domain-collection.workers.dev';

// Pricing plans - FREE FOR EVERYONE
const PLANS = [
  {
    name: 'Free',
    price: 0,
    period: 'forever',
    features: [
      'All SDK modules included',
      'Firewall, Scanner, Cracker',
      'Exploit framework',
      'C2 framework', 
      'Forensics toolkit',
      'Recon & OSINT tools',
      'Unlimited machines',
      'Community support',
      'All future updates'
    ],
    cta: 'Get Free License',
    popular: true,
    id: 'free'
  }
];

function App() {
  const [activeSection, setActiveSection] = useState('home');
  const [licenseKey, setLicenseKey] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [email, setEmail] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isRequesting, setIsRequesting] = useState(false);
  const [requestResult, setRequestResult] = useState(null);

  const validateLicense = async () => {
    setIsValidating(true);
    setValidationResult(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/licenses/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ license_key: licenseKey })
      });
      
      const data = await response.json();
      setValidationResult(data);
    } catch (error) {
      setValidationResult({ valid: false, message: 'Connection error. Please try again.' });
    }
    
    setIsValidating(false);
  };

  const requestFreeLicense = async () => {
    if (!email) return;
    
    setIsRequesting(true);
    setRequestResult(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/licenses/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      setRequestResult(data);
    } catch (error) {
      setRequestResult({ success: false, error: 'Connection error. Please try again.' });
    }
    
    setIsRequesting(false);
  };

  const handlePurchase = async (plan) => {
    setSelectedPlan(plan);
    setActiveSection('signup');
  };

  return (
    <div className="app">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-brand" onClick={() => setActiveSection('home')}>
          <span className="logo">🥑</span>
          <span className="brand-text">Avocado</span>
        </div>
        <div className="nav-links">
          <a onClick={() => setActiveSection('home')}>Home</a>
          <a onClick={() => setActiveSection('features')}>Features</a>
          <a onClick={() => setActiveSection('pricing')}>Pricing</a>
          <a onClick={() => setActiveSection('docs')}>Docs</a>
          <a onClick={() => setActiveSection('validate')} className="nav-btn">Validate License</a>
        </div>
      </nav>

      {/* Hero Section */}
      {activeSection === 'home' && (
        <section className="hero">
          <div className="hero-content">
            <h1 className="hero-title">
              The Most Powerful<br/>
              <span className="gradient-text">Cybersecurity SDK</span>
            </h1>
            <p className="hero-subtitle">
              Build enterprise firewalls, vulnerability scanners, and exploit frameworks 
              in <strong>one line of code</strong>. Avocado is the programming language 
              designed for security professionals.
            </p>
            <div className="hero-cta">
              <button className="btn btn-primary" onClick={() => setActiveSection('pricing')}>
                Get Started
              </button>
              <button className="btn btn-secondary" onClick={() => setActiveSection('docs')}>
                View Documentation
              </button>
            </div>
            <div className="hero-code">
              <pre><code>{`// Deploy enterprise firewall in ONE LINE
let fw = Firewall::enterprise("10.0.0.0/8");
fw.start();

// Full security audit in ONE LINE
let report = Scanner::full_audit("target.com");
report.print();`}</code></pre>
            </div>
          </div>
          <div className="hero-stats">
            <div className="stat">
              <span className="stat-number">15+</span>
              <span className="stat-label">Security Modules</span>
            </div>
            <div className="stat">
              <span className="stat-number">1</span>
              <span className="stat-label">Line Deploy</span>
            </div>
            <div className="stat">
              <span className="stat-number">∞</span>
              <span className="stat-label">Possibilities</span>
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      {activeSection === 'features' && (
        <section className="features">
          <h2 className="section-title">One-Liner Security Toolkit</h2>
          <p className="section-subtitle">Every module is designed for maximum power with minimum code</p>
          
          <div className="feature-grid">
            <div className="feature-card">
              <div className="feature-icon">🔥</div>
              <h3>Firewall</h3>
              <p>Enterprise-grade stateful firewall with DPI, DDoS protection, and rate limiting.</p>
              <code>Firewall::enterprise("192.168.0.0/16")</code>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">🎯</div>
              <h3>Scanner</h3>
              <p>Full vulnerability assessment with port scanning, service detection, and CVE matching.</p>
              <code>Scanner::full_audit("target.com")</code>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">🔓</div>
              <h3>Cracker</h3>
              <p>GPU-accelerated password cracking with dictionary, brute force, and rule-based attacks.</p>
              <code>Cracker::gpu_accelerated()</code>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">💥</div>
              <h3>Exploit</h3>
              <p>Rapid exploit development with ROP chain building, shellcode generation, and encoding.</p>
              <code>Exploit::buffer_overflow("./bin", 264)</code>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">🎮</div>
              <h3>C2</h3>
              <p>Command & Control infrastructure with encrypted channels, DNS tunneling, and HTTP C2.</p>
              <code>C2::server(4444)</code>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">🔬</div>
              <h3>Forensics</h3>
              <p>Memory analysis, disk forensics, credential extraction, and timeline generation.</p>
              <code>Forensics::memory_dump("mem.raw")</code>
            </div>
          </div>
        </section>
      )}

      {/* Pricing Section */}
      {activeSection === 'pricing' && (
        <section className="pricing">
          <h2 className="section-title">100% Free & Open</h2>
          <p className="section-subtitle">Avocado is free for everyone. Get your license key instantly.</p>
          
          <div className="pricing-grid" style={{maxWidth: '500px'}}>
            {PLANS.map((plan) => (
              <div key={plan.id} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
                {plan.popular && <div className="popular-badge">Full Access</div>}
                <h3 className="plan-name">{plan.name}</h3>
                <div className="plan-price">
                  <span className="price">Free</span>
                  <span className="period"> forever</span>
                </div>
                <ul className="plan-features">
                  {plan.features.map((feature, i) => (
                    <li key={i}>✓ {feature}</li>
                  ))}
                </ul>
                <button 
                  className="btn btn-primary"
                  onClick={() => handlePurchase(plan)}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* License Validation */}
      {activeSection === 'validate' && (
        <section className="validate">
          <h2 className="section-title">Validate Your License</h2>
          <p className="section-subtitle">Enter your license key to check its status</p>
          
          <div className="validate-form">
            <input
              type="text"
              placeholder="AVO-XXXX-XXXX-XXXX-XXXX"
              value={licenseKey}
              onChange={(e) => setLicenseKey(e.target.value.toUpperCase())}
              className="license-input"
            />
            <button 
              className="btn btn-primary"
              onClick={validateLicense}
              disabled={isValidating || !licenseKey}
            >
              {isValidating ? 'Validating...' : 'Validate'}
            </button>
          </div>
          
          {validationResult && (
            <div className={`validation-result ${validationResult.valid ? 'valid' : 'invalid'}`}>
              {validationResult.valid ? (
                <>
                  <div className="result-icon">✓</div>
                  <h3>License Valid</h3>
                  <p>Email: {validationResult.email}</p>
                  <p>Plan: {validationResult.plan}</p>
                  <p>Expires: {new Date(validationResult.expires).toLocaleDateString()}</p>
                  <p>Days Remaining: {validationResult.days_remaining}</p>
                  <div className="features-list">
                    <strong>Features:</strong>
                    <ul>
                      {validationResult.features?.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  </div>
                </>
              ) : (
                <>
                  <div className="result-icon">✗</div>
                  <h3>License Invalid</h3>
                  <p>{validationResult.message}</p>
                </>
              )}
            </div>
          )}
        </section>
      )}

      {/* Documentation */}
      {activeSection === 'docs' && (
        <section className="docs">
          <h2 className="section-title">Quick Start Guide</h2>
          
          <div className="doc-section">
            <h3>1. Get Your Free License</h3>
            <p style={{color: '#a0a0b0', marginBottom: '1rem'}}>Click "Get Free License" on the pricing page and enter your email. You'll receive your license key instantly.</p>
          </div>
          
          <div className="doc-section">
            <h3>2. Installation</h3>
            <pre><code>{`# Install Avocado with your license key
curl -sSL https://get.avocado-lang.com | bash -s -- YOUR_LICENSE_KEY

# Or download manually for your platform
# Linux: avocado-linux-x86_64.tar.gz
# macOS: avocado-darwin-aarch64.tar.gz
# Windows: avocado-windows-x86_64.zip`}</code></pre>
          </div>
          
          <div className="doc-section">
            <h3>3. Install SDK Modules</h3>
            <pre><code>{`# Install all modules (free!)
avc install --all

# Or install specific modules
avc install firewall
avc install scanner
avc install exploit`}</code></pre>
          </div>
          
          <div className="doc-section">
            <h3>4. Create Your First Project</h3>
            <pre><code>{`# Generate a project from template
avc generate scanner my_scanner
cd my_scanner

# Build and run
avc build
avc run`}</code></pre>
          </div>
          
          <div className="doc-section">
            <h3>Example: Network Scanner</h3>
            <pre><code>{`use avocado::sdk::scanner::*;

fn main() {
    // Full security audit in ONE LINE
    let report = Scanner::full_audit("target.com");
    
    // Print results
    report.print();
    
    // Save to file
    report.save("audit.json").unwrap();
}`}</code></pre>
          </div>
          
          <div className="doc-section">
            <h3>Example: Enterprise Firewall</h3>
            <pre><code>{`use avocado::sdk::firewall::*;

fn main() {
    // Deploy firewall in ONE LINE
    let mut fw = Firewall::enterprise("192.168.0.0/16");
    
    // Add custom rules
    fw.allow_inbound_port(Protocol::TCP, 22);
    fw.allow_inbound_port(Protocol::TCP, 443);
    
    // Enable protections
    fw.add_preset(Preset::BlockSQLInjection);
    fw.add_preset(Preset::BlockXSS);
    
    // Start
    fw.start().unwrap();
}`}</code></pre>
          </div>
        </section>
      )}

      {/* Sign Up (Free Plan) */}
      {activeSection === 'signup' && (
        <section className="signup">
          <h2 className="section-title">Get Your Free License</h2>
          <p className="section-subtitle">Enter your email to receive your license key instantly</p>
          
          {!requestResult?.success ? (
            <>
              <div className="signup-form">
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="email-input"
                  disabled={isRequesting}
                />
                <button 
                  className="btn btn-primary"
                  onClick={requestFreeLicense}
                  disabled={isRequesting || !email}
                >
                  {isRequesting ? 'Sending...' : 'Get License Key'}
                </button>
              </div>
              
              {requestResult?.error && (
                <div style={{color: '#ff4757', marginTop: '1rem'}}>
                  {requestResult.error}
                </div>
              )}
              
              <p className="signup-note">
                Your license key will be sent to your email instantly.<br/>
                100% free, forever. No credit card required.
              </p>
            </>
          ) : (
            <div className="validation-result valid" style={{maxWidth: '500px', margin: '0 auto'}}>
              <div className="result-icon">✓</div>
              <h3>License Key Sent!</h3>
              <p>Check your email at <strong>{email}</strong></p>
              <p style={{marginTop: '1rem', fontSize: '0.9rem'}}>
                Didn't receive it? Check your spam folder or try again.
              </p>
              <button 
                className="btn btn-secondary" 
                style={{marginTop: '1.5rem'}}
                onClick={() => {
                  setRequestResult(null);
                  setEmail('');
                }}
              >
                Request Another
              </button>
            </div>
          )}
        </section>
      )}

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-brand">
            <span className="logo">🥑</span>
            <span>Avocado</span>
            <p>The Cybersecurity Programming Language</p>
          </div>
          <div className="footer-links">
            <div className="footer-col">
              <h4>Product</h4>
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <a href="#docs">Documentation</a>
            </div>
            <div className="footer-col">
              <h4>Company</h4>
              <a href="#">About</a>
              <a href="#">Blog</a>
              <a href="#">Careers</a>
            </div>
            <div className="footer-col">
              <h4>Legal</h4>
              <a href="#">Privacy Policy</a>
              <a href="#">Terms of Service</a>
              <a href="#">License Agreement</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2025 Aikarotech Canada Inc. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
